import React from 'react';
import * as Routes from '@/modules/routes';
import Pill from '@/components/ui/Pill';
import Text from '@/components/ui/Text';
import cn from '@/utils/cn';
import { formatDuration } from '../LiveSessionsMonitor/tableShared';
import type { LiveSessionDetail } from '../LiveSessionsMonitor/types';
import { absoluteLabel } from './timeFormat';
import type { ParticipantsResponse } from './types';

interface HeaderCardProps {
  detail: LiveSessionDetail | undefined;
  eventCount: number;
  isLive: boolean;
  participants: ParticipantsResponse | undefined;
  sessionId: string;
}

const END_REASON_LABEL: Record<string, string> = {
  error: 'Error',
  teacher_ended: 'Ended by teacher',
  timeout: 'Timed out',
};

const Stat = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="tw-flex tw-flex-col tw-gap-0.5">
    <Text className="tw-text-very-dark-grey tw-uppercase tw-tracking-wide" size="xs" tag="span">{label}</Text>
    <Text className="tw-text-tci-black" size="sm" tag="span" weight="bold">{value}</Text>
  </div>
);

const HeaderCard = ({ detail, eventCount, isLive, participants, sessionId }: HeaderCardProps) => {
  const joined = participants?.students.filter(s => s.participation).length ?? 0;
  const invited = participants?.students.filter(s => s.invited).length ?? 0;
  const startedAt = detail?.started_at ? new Date(detail.started_at) : null;

  return (
    <section
      aria-label="Session summary"
      className={cn(
        'tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-rounded-lg tw-p-5',
        'tw-flex tw-flex-col tw-gap-4',
      )}
    >
      <div className="tw-flex tw-items-start tw-justify-between tw-flex-wrap tw-gap-3">
        <div className="tw-flex tw-flex-col tw-gap-1">
          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
            <Text className="tw-text-tci-black tw-m-0" size="xl" tag="h1" variant="label">
              {detail?.slide_show.name ?? 'Live session'}
            </Text>
            {detail && (
              <Pill pillColor={detail.status === 'active' ? 'green' : 'grey'} solid={detail.status === 'active'}>
                <Text size="xs" tag="span" weight="bold">{detail.status === 'active' ? 'Active' : 'Ended'}</Text>
              </Pill>
            )}
            {detail?.end_reason && (
              <Pill outline pillColor={detail.end_reason === 'teacher_ended' ? 'grey' : 'yellow'}>
                <Text size="xs" tag="span">{END_REASON_LABEL[detail.end_reason] ?? detail.end_reason}</Text>
              </Pill>
            )}
            {isLive && (
              <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-very-dark-grey">
                <span aria-hidden className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-green-500 tw-animate-pulse" />
                Live
              </span>
            )}
          </div>
          <Text className="tw-text-very-dark-grey tw-m-0" size="sm">
            {detail?.teacher.name ?? '—'} · {detail?.program.name ?? '—'}
          </Text>
          <Text className="tw-text-very-dark-grey tw-m-0 tw-font-mono" size="xs">{sessionId}</Text>
        </div>
        <a className="tw-text-sm tw-text-tci-purple hover:tw-underline" href={Routes.admin_live_sessions_path()}>
          ← All live sessions
        </a>
      </div>

      <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-4">
        <Stat
          label="Started"
          value={startedAt ? `${startedAt.toLocaleDateString()} ${absoluteLabel(detail!.started_at)}` : '—'}
        />
        <Stat
          label="Duration"
          value={detail?.duration_seconds != null ? formatDuration(detail.duration_seconds) : 'In progress'}
        />
        <Stat label="Joined" value={`${joined} of ${invited} invited`} />
        <Stat label="Events" value={eventCount.toLocaleString()} />
        <Stat label="Teacher" value={participants?.teacher.name ?? detail?.teacher.name ?? '—'} />
        <Stat
          label="Teacher entry"
          value={participants?.teacher.entry
            ? `${participants.teacher.entry.entry_mode ?? '?'} · ${participants.teacher.entry.nav_type ?? '?'}`
            : '—'}
        />
      </div>
    </section>
  );
};

export default HeaderCard;
